import { useEffect, useRef } from "react";
import {
  Card,
  Code,
  Container,
  Divider,
  Flex,
  Group,
  List,
  SimpleGrid,
  Stack,
  Text,
  Title,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { IconApi, IconGauge, IconKey } from "@tabler/icons-react";
import { useMediaQuery } from "@mantine/hooks";
import { useMobileScreen } from "@/shared/hooks/useMobileScreen";
import { EndpointCard } from "./components/EndpointCard";
import SectionCard from "./components/SectionCard";
import WebhookSection from "./components/WebhookSection";
import { apiEndpoints } from "./apiEndpoints";

const DocsHero = ({ isMobile }: { isMobile: boolean }) => {
  const theme = useMantineTheme();
  const iconColor = theme.colors.R500?.[0] ?? theme.colors.blue[6];

  return (
    <Stack
      gap="md"
      align={isMobile ? "flex-start" : "center"}
      ta={isMobile ? "left" : "center"}>
      <Group justify={isMobile ? "flex-start" : "center"}>
        <IconApi
          size={isMobile ? 40 : 48}
          color={iconColor}
        />
      </Group>
      <Title
        order={1}
        size={isMobile ? "h2" : "h1"}
        c={theme.colors.plain_text[0]}
        style={{ fontFamily: theme.headings.fontFamily }}>
        API de Integração Super Agents
      </Title>
      <Text
        size="lg"
        c={theme.colors.plain_text[0]}
        maw={720}
        mx={isMobile ? undefined : "auto"}>
        Conecte sua IA de atendimento à nossa plataforma de conversas e tenha
        controle total sobre WhatsApp oficial/não oficial, escalação humana e
        monitoramento em tempo real.
      </Text>
    </Stack>
  );
};

const benefits = [
  {
    title: "🤖 Integração com sua IA",
    description:
      "Você já criou uma IA de atendimento? Conecte-a à nossa plataforma para ter visibilidade completa das conversas.",
  },
  {
    title: "👥 Atendimento Humano Integrado",
    description:
      "Escalone conversas para humanos de forma fluida, mantendo todo o histórico e contexto.",
  },
  {
    title: "📱 WhatsApp Sem Complicação",
    description:
      "Não se preocupe com integrações complexas do WhatsApp - cuidamos disso para você.",
  },
];

const ValuePropositionSection = ({
  isMobile,
  isTablet,
}: {
  isMobile: boolean;
  isTablet: boolean;
}) => {
  const theme = useMantineTheme();
  let columns = 3;

  if (isTablet) {
    columns = 2;
  }

  if (isMobile) {
    columns = 1;
  }

  return (
    <Card
      shadow="sm"
      padding="xl"
      radius="md"
      withBorder
      bg={theme.colors.background_1[0]}>
      <Stack gap="lg">
        <Title
          order={2}
          c={theme.colors.plain_text[0]}
          ta={isMobile ? "left" : "center"}>
          Por que usar nossa API?
        </Title>
        <SimpleGrid
          cols={columns}
          spacing="lg">
          {benefits.map((benefit) => (
            <Stack
              key={benefit.title}
              gap="xs">
              <Text
                fw={600}
                c={theme.colors.contrast_text[0]}>
                {benefit.title}
              </Text>
              <Text
                size="sm"
                c={theme.colors.plain_text[0]}>
                {benefit.description}
              </Text>
            </Stack>
          ))}
        </SimpleGrid>
      </Stack>
    </Card>
  );
};

const AuthenticationSection = () => {
  const theme = useMantineTheme();
  const iconColor = theme.colors.R500?.[0] ?? theme.colors.blue[6];
  const headerExample = `x-api-key: "[sua-chave-configurada]"`;

  return (
    <SectionCard
      title="Autenticação"
      icon={
        <IconKey
          size={20}
          color={iconColor}
          aria-hidden
        />
      }>
      <Text c={theme.colors.plain_text[0]}>
        Todas as requisições devem incluir sua chave de API no header:
      </Text>
      <Code
        block
        style={{ overflowX: "auto", width: "100%" }}>
        {headerExample}
      </Code>
      <Text
        size="sm"
        c={theme.colors.plain_text[0]}>
        Você pode obter sua chave de API nas configurações da sua conta.
      </Text>
    </SectionCard>
  );
};

const RateLimitSection = () => {
  const theme = useMantineTheme();
  const iconColor = theme.colors.R500?.[0] ?? theme.colors.blue[6];

  return (
    <SectionCard
      title="Rate limiting (limite de requisições)"
      icon={
        <IconGauge
          size={20}
          color={iconColor}
          aria-hidden
        />
      }>
      <Stack gap="xs">
        <Text c={theme.colors.plain_text[0]}>
          Para garantir estabilidade e proteger a infraestrutura, a API do
          Backend aplica políticas de <b>rate limiting</b> (limite de
          requisições).
        </Text>
        <List
          spacing="xs"
          size="sm"
          c={theme.colors.plain_text[0]}>
          <List.Item>
            Quando o limite é excedido, a API pode responder com{" "}
            <b>HTTP 429 (Too Many Requests)</b>.
          </List.Item>
          <List.Item>
            Para disparos em massa, use envio em <b>batch</b> (lotes menores) e
            controle de taxa (throttling). Como referência inicial, considere{" "}
            <b>~5 mensagens por segundo</b> e ajuste conforme seu cenário.
          </List.Item>
        </List>
      </Stack>
    </SectionCard>
  );
};

const EndpointsSection = () => {
  const theme = useMantineTheme();

  return (
    <Stack gap="lg">
      <Title
        order={2}
        c={theme.colors.plain_text[0]}>
        Endpoints Disponíveis
      </Title>
      <Stack gap="lg">
        {apiEndpoints.map((endpoint) => (
          <EndpointCard
            key={endpoint.endpoint}
            {...endpoint}
          />
        ))}
      </Stack>
    </Stack>
  );
};

const GettingStartedSection = () => {
  const theme = useMantineTheme();
  const steps = [
    "Obtenha sua chave de API nas configurações",
    "Configure sua instância do WhatsApp",
    "Teste os endpoints com os exemplos fornecidos",
    "Integre com sua aplicação",
  ];

  return (
    <SectionCard
      title="Começando"
      highlight>
      <List
        spacing="xs"
        size="sm"
        c={theme.colors.plain_text[0]}>
        {steps.map((step) => (
          <List.Item key={step}>{step}</List.Item>
        ))}
      </List>
    </SectionCard>
  );
};

const DocsPage = () => {
  const { isMobileScreen } = useMobileScreen();
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const initialColorScheme = useRef(colorScheme);
  const theme = useMantineTheme();
  const isTabletScreen = useMediaQuery("(max-width: 62em)");

  useEffect(() => {
    if (colorScheme !== "light") {
      setColorScheme("light");
    }
  }, [colorScheme, setColorScheme]);

  useEffect(() => {
    const originalColorScheme = initialColorScheme.current;
    return () => {
      setColorScheme(originalColorScheme);
    };
  }, [setColorScheme]);

  return (
    <Flex
      direction="column"
      mih="100vh"
      bg={theme.colors.background_3[0]}
      style={{ minWidth: 0, overflowX: "hidden" }}>
      <Container
        size={isMobileScreen ? "md" : "lg"}
        px={{ base: "md", sm: "xl" }}
        py={{ base: "lg", sm: "xl" }}
        w="100%"
        style={{ minWidth: 0 }}>
        <Stack
          gap="xl"
          style={{ minWidth: 0 }}>
          <DocsHero isMobile={isMobileScreen} />

          <Divider />

          <ValuePropositionSection
            isMobile={isMobileScreen}
            isTablet={Boolean(isTabletScreen) && !isMobileScreen}
          />

          <Divider />

          <AuthenticationSection />

          <WebhookSection />

          <EndpointsSection />

          <RateLimitSection />

          <GettingStartedSection />
        </Stack>
      </Container>
    </Flex>
  );
};

export default DocsPage;
